import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { Minus, Plus, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { money } from "@/lib/api";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotal } = useCart();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col bg-white" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl">Your cart ({items.reduce((s, i) => s + i.quantity, 0)})</SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 grid place-items-center text-center px-8">
            <div>
              <p className="text-[#86868B] mb-6">Your cart is empty.</p>
              <button className="btn-primary" onClick={() => setOpen(false)} data-testid="cart-empty-shop-btn">
                Start shopping
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto py-4 space-y-4">
              {items.map((i) => {
                const key = `${i.product_id}::${i.variant_sku}`;
                return (
                  <div key={key} className="flex gap-3 items-start" data-testid={`cart-line-${i.variant_sku}`}>
                    {i.image && (
                      <img
                        src={i.image}
                        alt={i.product_name}
                        className="w-20 h-20 rounded-xl object-cover bg-[#F5F5F7]"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-[#1D1D1F]">{i.product_name}</p>
                      <p className="text-xs text-[#86868B]">{i.variant_name}</p>
                      <p className="text-sm font-semibold mt-1">{money(i.unit_price)}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          aria-label="Decrease"
                          onClick={() => updateQty(key, i.quantity - 1)}
                          className="w-7 h-7 rounded-full border border-black/10 grid place-items-center hover:bg-[#F5F5F7]"
                          data-testid={`cart-dec-${i.variant_sku}`}
                        >
                          <Minus size={12} />
                        </button>
                        <span className="text-sm w-6 text-center" data-testid={`cart-qty-${i.variant_sku}`}>{i.quantity}</span>
                        <button
                          aria-label="Increase"
                          onClick={() => updateQty(key, i.quantity + 1)}
                          className="w-7 h-7 rounded-full border border-black/10 grid place-items-center hover:bg-[#F5F5F7]"
                          data-testid={`cart-inc-${i.variant_sku}`}
                        >
                          <Plus size={12} />
                        </button>
                        <button
                          onClick={() => removeItem(key)}
                          aria-label="Remove"
                          className="ml-auto text-[#86868B] hover:text-[#FF3B30] p-1"
                          data-testid={`cart-remove-${i.variant_sku}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-black/5 pt-4 pb-2 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-[#86868B]">Subtotal</span>
                <span className="font-semibold" data-testid="cart-subtotal">{money(subtotal)}</span>
              </div>
              <p className="text-xs text-[#86868B]">Shipping and taxes calculated at checkout. Free shipping over $50.</p>
              <Link
                to="/checkout"
                onClick={() => setOpen(false)}
                data-testid="cart-checkout-btn"
                className="btn-primary w-full block text-center"
              >
                Checkout
              </Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
